import { BrowserRouter as Router, NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import React, { useEffect, useState } from 'react';
import {
    LayoutDashboard,
    MapPin,
    Trophy,
    Dumbbell,
    Users,
    User,
    BookOpen,
    Clock,
    Bell,
    Megaphone,
    Image,
    Percent,
    Tag,
    Settings,
    LogOut,
    Menu,
    X,
    AlertCircle,
} from 'lucide-react';
import Dashboard from './pages/Dashboard';
import Banner from './pages/Banner';
import Partners from './pages/Partners';
import City from './pages/City';
import Boxes from './pages/Boxes';
import UserPage from './pages/user';
import Booking from './pages/Booking';
import OneSignal from 'react-onesignal';
import Login from './pages/login';
import Notification from './pages/notification';
import Amenities from './pages/Amenities';
import Sport from './pages/Sport';
import SettingsPage from './pages/Settings';
import Tournament from './pages/Tournament';
import Offer from './pages/Offer';
import PartnerCoupons from './pages/PartnerCoupons';
import Analytics from './pages/Analytics';
import Advertisement from './pages/Advertisement';
import AdvertisementBanner from './pages/AdvertisementBanner';
import DefaultSlots from './pages/DefaultSlots';
import { Toaster } from './components/ui/sonner';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './components/ui/dialog';
import { Button } from './components/ui/button';
import { cn } from './lib/utils';

type MenuItem = {
    name: string;
    navigation: string;
    icon: React.ReactNode;
};

type MenuSection = {
    title: string;
    items: MenuItem[];
};

const menuSections: MenuSection[] = [
    {
        title: "Overview",
        items: [
            { name: "Dashboard", navigation: "/", icon: <LayoutDashboard className="h-4 w-4" /> },
        ],
    },
    {
        title: "Catalog",
        items: [
            { name: "City", navigation: "/city", icon: <MapPin className="h-4 w-4" /> },
            { name: "Sports", navigation: "/sport", icon: <Trophy className="h-4 w-4" /> },
            { name: "Amenities(Facilities)", navigation: "/amenities", icon: <Dumbbell className="h-4 w-4" /> },
            { name: "Partners", navigation: "/partners", icon: <Users className="h-4 w-4" /> },
            { name: "Users", navigation: "/user", icon: <User className="h-4 w-4" /> },
        ],
    },
    {
        title: "Bookings",
        items: [
            { name: "Bookings", navigation: "/booking", icon: <BookOpen className="h-4 w-4" /> },
            { name: "Default slot management", navigation: "/default-slots", icon: <Clock className="h-4 w-4" /> },
            { name: "Tournaments", navigation: "/tournament", icon: <Trophy className="h-4 w-4" /> },
            { name: "Notifications", navigation: "/notification", icon: <Bell className="h-4 w-4" /> },
        ],
    },
    {
        title: "Marketing",
        items: [
            { name: "Advertisement", navigation: "/advertisement", icon: <Megaphone className="h-4 w-4" /> },
            { name: "Advertisement Banner", navigation: "/advertisementBanner", icon: <Megaphone className="h-4 w-4" /> },
            { name: "Banner", navigation: "/banner", icon: <Image className="h-4 w-4" /> },
            { name: "Offer(Discounts)", navigation: "/offer", icon: <Percent className="h-4 w-4" /> },
            { name: "Partner Coupons", navigation: "/partner-coupons", icon: <Tag className="h-4 w-4" /> },
        ],
    },
    {
        title: "System",
        items: [
            { name: "Settings", navigation: "/settings", icon: <Settings className="h-4 w-4" /> },
        ],
    },
];

function App() {
    const navigate = useNavigate();
    const [showLogoutDialog, setShowLogoutDialog] = useState(false);
    const [openSidebar, setOpenSidebar] = useState(false);
    const [onesignalId, setOnesignalId] = useState<any>();
    const location = useLocation();
    const isAuthenticated = !!localStorage.getItem("token");

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const sub = OneSignal.User?.PushSubscription;
            const id = sub?.id;
            setOnesignalId(id);
            if (id) console.log(id, "one signal user");
        } catch {
            setOnesignalId(undefined);
        }
    };

    const isLoginScreen = location.pathname === '/login';

    const logoutHandler = () => {
        setShowLogoutDialog(true);
    };

    const confirmLogout = () => {
        setShowLogoutDialog(false);
        localStorage.removeItem('admindetails');
        localStorage.removeItem('token');
        navigate("/login");
    };

    const cancelLogout = () => {
        setShowLogoutDialog(false);
    };

    return (
        <div className="min-h-screen bg-muted/30">
            <Toaster position="top-right" richColors />

            {!isLoginScreen && (
                <>
                    {/* Mobile overlay */}
                    {openSidebar && (
                        <div
                            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
                            onClick={() => setOpenSidebar(false)}
                        />
                    )}

                    {/* Sidebar */}
                    <aside
                        className={cn(
                            "fixed top-0 left-0 h-full w-64 bg-background border-r border-border flex flex-col z-40 transition-transform duration-200",
                            openSidebar ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
                        )}
                    >
                        <div className="px-5 py-5 border-b border-border flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground text-xs font-bold shrink-0">ET</div>
                                <div>
                                    <p className="text-sm font-semibold leading-none text-foreground">EasyTurf</p>
                                    <p className="text-xs text-muted-foreground">Admin portal</p>
                                </div>
                            </div>
                            <button
                                aria-label="Close sidebar"
                                onClick={() => setOpenSidebar(false)}
                                className="lg:hidden p-1 rounded-md hover:bg-accent"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-4">
                            {menuSections.map((section) => (
                                <div key={section.title}>
                                    <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                                        {section.title}
                                    </p>
                                    <ul className="space-y-0.5">
                                        {section.items.map((item) => (
                                            <li key={item.navigation}>
                                                <NavLink
                                                    to={item.navigation}
                                                    end={item.navigation === "/"}
                                                    className={({ isActive }) =>
                                                        cn(
                                                            "flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                                                            isActive
                                                                ? "bg-primary/10 text-primary"
                                                                : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                                                        )
                                                    }
                                                    onClick={() => setOpenSidebar(false)}
                                                >
                                                    {item.icon}
                                                    <span>{item.name}</span>
                                                </NavLink>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </nav>

                        <div className="px-4 py-3 border-t border-border">
                            <button
                                onClick={() => {
                                    setOpenSidebar(false);
                                    logoutHandler();
                                }}
                                className="flex items-center gap-2.5 w-full px-3 py-2 rounded-md text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors mb-2"
                            >
                                <LogOut className="h-4 w-4" />
                                <span>Logout</span>
                            </button>
                            <p className="text-xs text-muted-foreground">Developed by @Rvirji Technologies</p>
                        </div>
                    </aside>
                </>
            )}

            {/* Main content */}
            <div className={cn(!isLoginScreen && "lg:pl-64")}>
                {!isLoginScreen && (
                    <button
                        aria-label="Open sidebar"
                        onClick={() => setOpenSidebar(true)}
                        className="fixed left-4 top-4 z-20 lg:hidden p-2 bg-primary text-primary-foreground rounded-md shadow-lg"
                    >
                        <Menu className="h-5 w-5" />
                    </button>
                )}
                <div className={cn("w-full p-4", !isLoginScreen ? "pt-16 lg:pt-4" : "")}>
                    <Routes>
                        <Route
                            path="/login"
                            element={isAuthenticated ? <Navigate to="/" replace /> : <Login onesignalId={onesignalId} />}
                        />
                        <Route path="/" element={isAuthenticated ? <Dashboard /> : <Navigate to="/login" replace />} />
                        <Route path="/city" element={isAuthenticated ? <City /> : <Navigate to="/login" replace />} />
                        <Route path="/sport" element={isAuthenticated ? <Sport /> : <Navigate to="/login" replace />} />
                        <Route path="/amenities" element={isAuthenticated ? <Amenities /> : <Navigate to="/login" replace />} />
                        <Route path="/advertisement" element={isAuthenticated ? <Advertisement /> : <Navigate to="/login" replace />} />
                        <Route path="/banner" element={isAuthenticated ? <Banner /> : <Navigate to="/login" replace />} />
                        <Route path="/advertisementBanner" element={isAuthenticated ? <AdvertisementBanner /> : <Navigate to="/login" replace />} />
                        <Route path="/partners" element={isAuthenticated ? <Partners /> : <Navigate to="/login" replace />} />
                        <Route path="/boxes" element={isAuthenticated ? <Boxes /> : <Navigate to="/login" replace />} />
                        <Route path="/user" element={isAuthenticated ? <UserPage /> : <Navigate to="/login" replace />} />
                        <Route path="/booking" element={isAuthenticated ? <Booking /> : <Navigate to="/login" replace />} />
                        <Route path="/default-slots" element={isAuthenticated ? <DefaultSlots /> : <Navigate to="/login" replace />} />
                        <Route path="/tournament" element={isAuthenticated ? <Tournament /> : <Navigate to="/login" replace />} />
                        <Route path="/offer" element={isAuthenticated ? <Offer /> : <Navigate to="/login" replace />} />
                        <Route path="/partner-coupons" element={isAuthenticated ? <PartnerCoupons /> : <Navigate to="/login" replace />} />
                        <Route path="/notification" element={isAuthenticated ? <Notification /> : <Navigate to="/login" replace />} />
                        <Route path="/settings" element={isAuthenticated ? <SettingsPage /> : <Navigate to="/login" replace />} />
                        <Route path="/analytics" element={isAuthenticated ? <Analytics /> : <Navigate to="/login" replace />} />
                        <Route path="*" element={<Navigate to={isAuthenticated ? "/" : "/login"} replace />} />
                    </Routes>
                </div>
            </div>

            {/* Logout confirmation dialog */}
            <Dialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
                <DialogContent className="max-w-sm">
                    <DialogHeader className="items-center text-center">
                        <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
                        <DialogTitle>Confirm Logout</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to logout from EasyTurf Admin?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-row justify-center gap-2 sm:justify-center">
                        <Button variant="outline" onClick={cancelLogout} className="w-24">
                            No
                        </Button>
                        <Button variant="destructive" onClick={confirmLogout} className="w-24">
                            Yes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default function MainApp() {
    return (
        <Router>
            <App />
        </Router>
    );
}
